import React from 'react';
import CartDrawer from './CartDrawer';
import SplitText from './SplitText';

// Importar Orbitron desde Google Fonts
if (typeof document !== 'undefined' && !document.getElementById('orbitron-font')) {
  const link = document.createElement('link');
  link.id = 'orbitron-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Orbitron:wght@700;900&display=swap';
  document.head.appendChild(link);
}

const Navbar = ({ cartItems, onUpdateQuantity, onRemoveItem, onSearch, onGoHome }) => {
  const [cartOpen, setCartOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [cartShake, setCartShake] = React.useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);
  const [searchPulse, setSearchPulse] = React.useState(false);
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const [searchTimeout, setSearchTimeout] = React.useState(null);

  // Calcular la cantidad total de items en el carrito
  const totalItemsInCart = cartItems.reduce((total, item) => total + item.quantity, 0);

  // Estado para rastrear el número anterior de items
  const [prevTotalItems, setPrevTotalItems] = React.useState(0);

  React.useEffect(() => {
    // Solo activar el efecto si el número de items aumentó (se agregó un producto)
    if (totalItemsInCart > prevTotalItems) {
      setCartShake(true);
      const timer = setTimeout(() => setCartShake(false), 600);
      return () => clearTimeout(timer);
    }
    // Actualizar el número anterior
    setPrevTotalItems(totalItemsInCart);
  }, [totalItemsInCart, prevTotalItems]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // Limpiar timeout anterior
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Crear nuevo timeout para búsqueda con debounce
    const newTimeout = setTimeout(() => {
      onSearch(value);
    }, 300); // 300ms de delay
    
    setSearchTimeout(newTimeout);
  };

  const handleSectionClick = (section) => {
    setSearchTerm(''); // Limpiar búsqueda local
    onSearch(section);
    setShowMobileMenu(false);
  };

  const handleGoHome = () => {
    setSearchTerm('');
    onGoHome();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Limpiar timeout al desmontar
  React.useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Lista de secciones para la barra
  const NAV_SECTIONS = [
    'CELULARES',
    'ACCESORIOS',
    'APPLE',
    'NOTEBOOKS',
    'TABLETS',
    'AUDIO',
    'SMARTWATCHES',
    'ELECTRONICA',
    'CONSOLAS',
  ];

  return (
    <nav className="w-full bg-white shadow-md fixed top-0 left-0 z-50">
      <div className="container mx-auto flex items-center justify-between py-3 px-4">
        {/* Logo y nombre */}
        <div className="flex items-center gap-4">
          <button 
            onClick={handleGoHome}
            className="flex items-center text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 font-orbitron"
            style={{ background: 'none', border: 'none', padding: 0, margin: 0 }}
          >
            <SplitText text="LT Celulares" className="text-2xl font-bold m-0 p-0 font-orbitron" splitType="chars" />
          </button>
        </div>

        {/* Bloque central: Apartados (solo en escritorio) */}
        <div className="hidden md:flex items-center gap-8">
          {NAV_SECTIONS.map(section => (
            <button
              key={section}
              onClick={() => handleSectionClick(section)}
              className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
            >
              {section}
            </button>
          ))}
        </div>

        {/* Menú hamburguesa para móvil */}
        <div className="md:hidden flex items-center">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="p-2 rounded focus:outline-none focus:ring-2 focus:ring-gray-300"
            aria-label="Abrir menú"
          >
            <svg className="w-7 h-7 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Menú desplegable móvil */}
        {showMobileMenu && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex flex-col items-end">
            <div className="bg-white w-64 shadow-xl rounded-l-xl mt-0 h-full flex flex-col">
              <button
                onClick={() => setShowMobileMenu(false)}
                className="self-end p-4 text-gray-700 hover:text-black focus:outline-none"
                aria-label="Cerrar menú"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex flex-col gap-2 px-6 py-4">
                {NAV_SECTIONS.map(section => (
                  <button
                    key={section}
                    onClick={() => handleSectionClick(section)}
                    className="text-base font-semibold text-gray-800 hover:text-black py-2 text-left w-full border-b border-gray-100"
                  >
                    {section}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bloque derecho: Búsqueda + Carrito */}
        <div className="flex items-center gap-1">
          {/* Input de búsqueda solo en escritorio */}
          <div className="relative hidden md:block">
            <input
              type="text"
              placeholder="Buscar productos..."
              className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Botón de favoritos a la izquierda del carrito */}
          <button
            onClick={() => onSearch('FAVORITOS')}
            className="p-2 text-gray-700 hover:text-red-500 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
            aria-label="Ver favoritos"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="#ef4444" viewBox="0 0 24 24" className="h-6 w-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.1 21.55l-1.1-1.01C5.14 15.24 2 12.39 2 8.5 2 5.5 4.5 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.5 3 22 5.5 22 8.5c0 3.89-3.14 6.74-8.9 12.04l-1 1.01z"/>
            </svg>
          </button>

          {/* Input de búsqueda en móvil */}
          {mobileSearchOpen ? (
            <div className="relative flex items-center w-44 sm:w-56">
              <input
                type="text"
                autoFocus
                placeholder="Buscar..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all text-base"
                value={searchTerm}
                onChange={handleSearchChange}
                onBlur={() => setMobileSearchOpen(false)}
              />
              <button
                className="absolute right-2 text-gray-400 hover:text-gray-700 focus:outline-none"
                onMouseDown={e => { e.preventDefault(); setMobileSearchOpen(false); }}
                tabIndex={-1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <>
              {/* Ícono de búsqueda solo en móvil */}
              <button 
                className={`block md:hidden p-2 text-gray-700 hover:text-gray-900 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 ${searchPulse ? 'animate-pulse-scale' : ''}`}
                onClick={() => {
                  setSearchPulse(true);
                  setTimeout(() => {
                    setSearchPulse(false);
                    setMobileSearchOpen(true);
                  }, 200);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {/* Carrito solo si no está el input de búsqueda */}
              <button 
                onClick={() => setCartOpen(true)}
                className={`relative p-2 text-gray-700 hover:text-gray-900 rounded-md transition-colors transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 ${cartShake ? 'animate-shake' : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {totalItemsInCart > 0 && (
                  <span className={`absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black text-xs text-white font-bold transition-all duration-300 ${cartShake ? 'animate-pulse scale-125' : ''}`}>
                    {totalItemsInCart}
                  </span>
                )}
              </button>
            </>
          )}
        </div>
      </div>
      <CartDrawer 
        isOpen={cartOpen} 
        onClose={() => setCartOpen(false)} 
        cartItems={cartItems} 
        onUpdateQuantity={onUpdateQuantity}
        onRemoveItem={onRemoveItem}
      />
      
      {/* Estilos CSS para animaciones */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        
        .animate-shake {
          animation: shake 0.6s ease-in-out;
        }
      `}</style>
    </nav>
  );
};

export default Navbar;